import {
  AuthResponse,
  CustodyApproval,
  CustodyNonceResponse,
  CustodySignError,
  CustodySignRequest,
  CustodySignResponse,
  CustodySessionProof,
  custodyApprovalMessage,
  custodyIntentHash,
  INTENT_VERSION,
  type CustodyIntent,
  type CustodyApproval as CustodyApprovalValue,
  type CustodySessionProof as CustodySessionProofValue,
} from "@redchain/contracts";
import type { Wallet, WalletAccount } from "@wallet-standard/base";
import { SolanaSignIn, SolanaSignMessage, type SolanaSignInFeature, type SolanaSignInInput, type SolanaSignMessageFeature } from "@solana/wallet-standard-features";
import { getBase58Decoder, getBase58Encoder, type Transaction } from "@solana/kit";
import { z } from "zod";
import { ApiRequestError, api } from "../../api/client";
import { solanaChain } from "../../env";
import { bytesToBase64, decodeWireTransaction, encodeWireTransaction } from "../tx";
import { requestApprovalSignature } from "./approval";
import { describeValueMove } from "./valueMove";

const SESSION_KEY = "redchain.custodySession";

/**
 * Committed custodial routes (prefix /v1):
 * POST /custody/accounts { connectedWallet } -> { gameAccount, playerIndex, created }
 * POST /custody/nonce CustodyNonceRequest
 * POST /custody/verify AuthVerifyRequest -> AuthResponse for the game account
 * POST /custody/sign CustodySignRequest -> { signature } or CustodySignError
 * GET  /custody/deposit-address -> { depositAddress, ... }
 * POST /custody/withdraw { ...payload, approval } (api adds gameAccount)
 */

const OpenedAccount = z.object({
  gameAccount: z.string().min(32),
  playerIndex: z.number().int(),
  created: z.boolean(),
});

const DepositAddressBody = z.object({ depositAddress: z.string().min(32) }).passthrough();

const LooseMe = z
  .object({
    wallet: z.string().optional(),
    walletKind: z.string().optional(),
    registered: z.boolean().optional(),
    gameBalance: z.string().optional(),
    rsol: z.string().nullable().optional(),
    rsolBalance: z.string().nullable().optional(),
  })
  .passthrough();

export type LooseMe = z.infer<typeof LooseMe> & { rsol: string | null };

let sessionProof: CustodySessionProofValue | null = readStoredSession();

function gameAccountFromSession(): string {
  const message = currentCustodySession()?.message ?? "";
  const line = message.split("\n").find((entry) => entry.startsWith("Game account: "));
  const gameAccount = line?.slice("Game account: ".length).trim();
  if (!gameAccount) throw new Error("Sign in again before withdrawing");
  return gameAccount;
}

function bytesToBase58(bytes: Uint8Array): string {
  return getBase58Decoder().decode(bytes);
}

function readStoredSession(): CustodySessionProofValue | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    return CustodySessionProof.parse(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function currentCustodySession(): CustodySessionProofValue | null {
  return sessionProof;
}

export function rememberCustodySession(connectedWallet: string, message: string, signatureB58: string): void {
  const session = CustodySessionProof.parse({ message, signature: signatureB58, connectedWallet });
  sessionProof = session;
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

export function clearCustodySession(): void {
  sessionProof = null;
  sessionStorage.removeItem(SESSION_KEY);
}

/** `/players/me` without PlayerMe, so `walletKind: "custodial"` and an optional rSOL field still load. */
export async function loadLooseMe(): Promise<LooseMe> {
  const parsed = LooseMe.parse(await api.request<unknown>("GET", "/players/me"));
  return { ...parsed, rsol: parsed.rsol ?? parsed.rsolBalance ?? null };
}

export async function loadDepositAddress(): Promise<string> {
  const body = DepositAddressBody.parse(await api.request<unknown>("GET", "/custody/deposit-address"));
  return body.depositAddress;
}

export function approvalExpiryUtc(minutes = 10): string {
  const d = new Date(Date.now() + minutes * 60_000);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())} UTC`;
}

async function signApprovalText(wallet: Wallet, account: WalletAccount, message: string): Promise<string> {
  const feature = (wallet.features as Partial<SolanaSignMessageFeature>)[SolanaSignMessage];
  if (!feature) throw new Error(`${wallet.name} doesn't support message signing`);
  const approved = await requestApprovalSignature(message);
  if (!approved) throw new Error("Approval cancelled");
  const [out] = await feature.signMessage({ account, message: new TextEncoder().encode(message) });
  if (!out) throw new Error("Approval was cancelled");
  return bytesToBase58(new Uint8Array(out.signature));
}

export async function buildApproval(
  wallet: Wallet,
  account: WalletAccount,
  summary: string,
  intent: CustodyIntent,
): Promise<CustodyApprovalValue> {
  const approvalId = crypto.randomUUID();
  const expiresUtc = approvalExpiryUtc();
  const message = custodyApprovalMessage({
    summary,
    approvalId,
    expiresUtc,
    intentHash: custodyIntentHash(intent),
  });
  const signature = await signApprovalText(wallet, account, message);
  return CustodyApproval.parse({
    message,
    signature,
    approvalId,
    connectedWallet: account.address,
  });
}

function applyGameSignature(bytes: Uint8Array, gameAccount: string, signatureB58: string): Uint8Array {
  const tx = decodeWireTransaction(bytes);
  const sig = new Uint8Array(getBase58Encoder().encode(signatureB58));
  if (sig.length !== 64) throw new Error("Custody signature was not 64 bytes");
  if (!(gameAccount in tx.signatures)) throw new Error("This transaction isn't for your game account");
  const signatures = { ...tx.signatures, [gameAccount]: sig };
  return encodeWireTransaction({ ...tx, signatures } as Transaction);
}

function signError(e: unknown): Error {
  if (!(e instanceof ApiRequestError)) return e instanceof Error ? e : new Error(String(e));
  const parsed = CustodySignError.safeParse(e.body);
  if (!parsed.success) return e;
  return new Error(parsed.data.detail ?? parsed.data.error);
}

/**
 * Sign In With Solana against the custody nonce, then POST /custody/verify.
 * The signed message names the game account.
 */
export async function signInCustodial(wallet: Wallet, account: WalletAccount): Promise<AuthResponse> {
  const feature = (wallet.features as Partial<SolanaSignInFeature>)[SolanaSignIn];
  if (!feature) throw new Error(`${wallet.name} doesn't support Sign In With Solana`);
  const opened = OpenedAccount.parse(
    await api.request<unknown>("POST", "/custody/accounts", { json: { connectedWallet: account.address }, auth: false }),
  );
  const nonce = CustodyNonceResponse.parse(
    await api.request<unknown>("POST", "/custody/nonce", {
      json: { connectedWallet: account.address, gameAccount: opened.gameAccount },
      auth: false,
    }),
  );
  const input: SolanaSignInInput = {
    domain: window.location.host,
    address: account.address,
    statement: `Game account: ${opened.gameAccount}`,
    nonce: nonce.nonce,
    issuedAt: new Date().toISOString(),
    expirationTime: nonce.sessionExpiresAt ?? new Date(Date.now() + 12 * 60 * 60 * 1000 - 60_000).toISOString(),
    ...(solanaChain === "solana:localnet" ? {} : { chainId: solanaChain }),
  };
  const [out] = await feature.signIn(input);
  if (!out) throw new Error("Sign-in was cancelled");
  const message = new TextDecoder().decode(out.signedMessage);
  rememberCustodySession(account.address, message, bytesToBase58(new Uint8Array(out.signature)));
  const proof = {
    signInInput: input as unknown as Record<string, unknown>,
    signInOutput: {
      account: { address: out.account.address, publicKey: bytesToBase64(new Uint8Array(out.account.publicKey)) },
      signedMessage: bytesToBase64(new Uint8Array(out.signedMessage)),
      signature: bytesToBase64(new Uint8Array(out.signature)),
    },
  };
  const res = AuthResponse.parse(await api.request<unknown>("POST", "/custody/verify", { json: proof, auth: false }));
  api.setToken(res.accessToken);
  return { ...res, wallet: res.wallet ?? opened.gameAccount };
}

/** POST /custody/sign. Value-moving transactions get an approval before the request is sent. */
export async function relaySignTransaction(
  wallet: Wallet,
  account: WalletAccount,
  gameAccount: string,
  bytes: Uint8Array,
): Promise<Uint8Array> {
  if (!sessionProof) throw new Error("Sign in again before signing");
  const move = await describeValueMove(bytes, gameAccount, account.address);
  const approval = move ? await buildApproval(wallet, account, move.summary, move.intent) : undefined;
  const body = CustodySignRequest.parse({
    gameAccount,
    transaction: bytesToBase64(bytes),
    session: sessionProof,
    ...(approval ? { approval } : {}),
  });
  const post = () => api.request<unknown>("POST", "/custody/sign", { json: body });
  try {
    const res = CustodySignResponse.parse(await post());
    return applyGameSignature(bytes, gameAccount, res.signature);
  } catch (e) {
    if (!lostCustodySession(e)) throw signError(e);
    clearCustodySession();
    const signedIn = await signInCustodial(wallet, account);
    const refreshed = CustodySignRequest.parse({
      ...body,
      gameAccount: signedIn.wallet ?? gameAccount,
      session: currentCustodySession(),
    });
    try {
      const res = CustodySignResponse.parse(await api.request<unknown>("POST", "/custody/sign", { json: refreshed }));
      return applyGameSignature(bytes, refreshed.gameAccount, res.signature);
    } catch (again) {
      throw signError(again);
    }
  }
}

function lostCustodySession(e: unknown): boolean {
  if (!(e instanceof ApiRequestError)) return false;
  const parsed = CustodySignError.safeParse(e.body);
  return parsed.success && (parsed.data.error === "expired_session" || parsed.data.error === "no_session");
}

/** Withdrawal approval, then POST /custody/withdraw. The api adds gameAccount. */
export async function submitWithdrawal(input: {
  wallet: Wallet;
  account: WalletAccount;
  asset: "rsol" | "rcd";
  amount: string;
  destination: string;
}): Promise<{ signature?: string }> {
  const label = input.asset === "rsol" ? "rSOL" : "RCD";
  const summary = `Withdraw ${input.amount} ${label} to ${input.destination}`;
  const gameAccount = gameAccountFromSession();
  const intent: CustodyIntent = {
    version: INTENT_VERSION,
    action: "withdraw",
    gameAccount,
    connectedWallet: input.account.address,
    params: {
      assetOrMint: input.asset,
      amount: input.amount,
      destination: input.account.address,
    },
  };
  const approval = await buildApproval(input.wallet, input.account, summary, intent);
  const payload = { asset: input.asset, amount: input.amount, destination: input.destination, approval };
  let body: unknown;
  try {
    body = await api.request<unknown>("POST", "/custody/withdraw", { json: payload });
  } catch (e) {
    if (!lostCustodySession(e)) throw e;
    clearCustodySession();
    await signInCustodial(input.wallet, input.account);
    body = await api.request<unknown>("POST", "/custody/withdraw", { json: payload });
  }
  const parsed = z.object({ signature: z.string().optional() }).passthrough().safeParse(body);
  return { signature: parsed.success ? parsed.data.signature : undefined };
}
